import { lazy } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { AppChrome } from "@/components/AppChrome";
import { MigrationDialog } from "@/components/MigrationDialog";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { ProfileProvider, useProfilesContext } from "@/context/ProfileContext";
import { SidebarLayout } from "@/layouts/SidebarLayout";
import { loadDashboardSettings } from "@/lib/dashboardSettings";

// Route-level code splitting: each page is its own chunk.
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const PrivacyInspectorPage = lazy(() => import("@/pages/PrivacyInspectorPage"));
const TransparencyPage = lazy(() => import("@/pages/TransparencyPage"));
const PrivacyShowdownPage = lazy(() => import("@/pages/PrivacyShowdownPage"));
const QualityTrendsPage = lazy(() => import("@/pages/QualityTrendsPage"));
const SuggestionsPage = lazy(() => import("@/pages/SuggestionsPage"));
const MarginCalculatorPage = lazy(() => import("@/pages/MarginCalculatorPage"));
const BulkEditPage = lazy(() => import("@/pages/BulkEditPage"));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage"));
const SavedSearchesPage = lazy(() => import("@/pages/SavedSearchesPage"));
const VendorDrillDownPage = lazy(() => import("@/pages/VendorDrillDownPage"));
const QuotesPage = lazy(() => import("@/pages/QuotesPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ProfilesPage = lazy(() => import("@/pages/ProfilesPage"));
const ProfileDetailPage = lazy(() => import("@/pages/ProfileDetailPage"));
const UploadPage = lazy(() => import("@/pages/UploadPage"));
const ProcessPage = lazy(() => import("@/pages/ProcessPage"));
const ReportPage = lazy(() => import("@/pages/ReportPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ConnectionsPage = lazy(() => import("@/pages/ConnectionsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

/** `/search?config=<encoded>` → shareable-link entry point; redirects into the Process page. */
function SearchRedirect(): JSX.Element {
  const [params] = useSearchParams();
  const config = params.get("config");
  return <Navigate to={config ? `/process?search=${config}` : "/process"} replace />;
}

/** Landing route: Dashboard when a profile is active (unless configured to Profiles). */
function IndexRedirect(): JSX.Element | null {
  const { activeProfile, isLoading } = useProfilesContext();
  if (isLoading) return null;
  if (!activeProfile) return <Navigate to="/profiles" replace />;
  const landing = loadDashboardSettings().defaultLanding;
  return <Navigate to={landing === "profiles" ? "/profiles" : "/dashboard"} replace />;
}

export default function App(): JSX.Element {
  return (
    <AppProvider>
      <ProfileProvider>
        <Routes>
          <Route element={<SidebarLayout />}>
            <Route index element={<IndexRedirect />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
            <Route path="profiles/:profileId" element={<ProfileDetailPage />} />
            <Route path="upload" element={<UploadPage />} />
            <Route path="process" element={<ProcessPage />} />
            <Route path="report" element={<ReportPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="connections" element={<ConnectionsPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="bulk-edit" element={<BulkEditPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="saved-searches" element={<SavedSearchesPage />} />
            <Route path="vendors" element={<VendorDrillDownPage />} />
            <Route path="search" element={<SearchRedirect />} />
            <Route path="privacy-inspector" element={<PrivacyInspectorPage />} />
            <Route path="transparency" element={<TransparencyPage />} />
            <Route path="privacy-showdown" element={<PrivacyShowdownPage />} />
            <Route path="quality-trends" element={<QualityTrendsPage />} />
            <Route path="suggestions" element={<SuggestionsPage />} />
            <Route path="margin-calculator" element={<MarginCalculatorPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <AppChrome />
        <MigrationDialog />
        <OnboardingDialog />
        <Toaster />
      </ProfileProvider>
    </AppProvider>
  );
}
